import { RegionFeatures } from './region-features.model';
import { RegionFeatureStatus } from './region-feature-status.model';
import { DriverAssignmentMode } from './driver-assignment-mode.model';
import { RegionFeatureOverrideAbility } from './region-feature-override-ability.model';
import { ProofOfDeliveryRule } from './pod-rule.model';
import { InheritableOnOffFeature , OnOffFeature} from './on-off-feature.model';
import { PhotoPodRule } from './photo-pod.rule';
import { OrderType } from './order-type.model';
import { CmaLiteDeliveryRule } from './cma-lite-delivery-rule.model';
import { AutoGroupCollections } from './auto-group-collections.model';

export class FeatureProfile {

  public id: string;
  public name: string;

  public loadTracking                                 = true;
  public hotspotTracking                              = false;
  public allowLoadsSearchFiltering                    = false;
  public trailerYardManagement                        = false;
  public multiCollections                             = false;
  public multiDeliveries                              = false;
  public tipAndReload                                 = false;
  public tmsLoadsSynchronization                      = false;
  public cabinLicensePlateRequired                    = true;
  public sapSignaturesSending                         = false;
  public assetManagement                              = false;
  public quantitiesUpdateTimer                        = false;
  public driverAssignmentModeFeature                  = DriverAssignmentMode.INHERITED;
  public proofOfDeliveryRuleFeature                   = ProofOfDeliveryRule.BOTH;
  public photoPodRuleFeature                          = PhotoPodRule.ONLY_AT_THE_DROP_LOCATION;
  public dropAndHookFeature                           = InheritableOnOffFeature.INHERITED;
  public automaticTripCreationFeature                 = InheritableOnOffFeature.INHERITED;
  public automaticTripCreationOrderTypes: OrderType[] = [];
  public sapMultiCollectionsCheckin                   = InheritableOnOffFeature.INHERITED;
  public autoGroupCollectionsFeature                  = AutoGroupCollections.INHERITED;
  public multiAccountsFeature                         = InheritableOnOffFeature.INHERITED;
  public overdueNotificationFeature                   = InheritableOnOffFeature.INHERITED;
  public handoverManagementFeature                    = InheritableOnOffFeature.INHERITED;
  public hideVehicleIdOnDriverInformationFeature      = InheritableOnOffFeature.INHERITED;
  public podValidationManufacturerSyncFeature         = InheritableOnOffFeature.INHERITED;
  public podValidationMrSyncFeature                   = InheritableOnOffFeature.INHERITED;
  public tripCreatedWebhookEnrichedDataFeature        = InheritableOnOffFeature.INHERITED;
  public useCmaLiteOverCicoFeature                    = OnOffFeature.OFF;
  public useCmaLiteOverCicoDeliveryRuleFeature        = CmaLiteDeliveryRule.Z2_INTERNATIONALS_ONLY;
  public sortLoadsByTripIdFeature                     = InheritableOnOffFeature.INHERITED;
  public sortStopsByBlueyonderOrderFeature            = InheritableOnOffFeature.INHERITED;
  public etaComputationFeature                        = InheritableOnOffFeature.INHERITED;
  public displayAutomaticEtaControlFeature            = InheritableOnOffFeature.INHERITED;
  public mrDropQuantityUpdateFeature                  = InheritableOnOffFeature.INHERITED;
  public retrieveCarrierEtaFromByFeature              = InheritableOnOffFeature.INHERITED;
  public communicatePickEtaForMrFeature               = InheritableOnOffFeature.INHERITED;

  static fromDto(p: any): FeatureProfile {
    if (!p) {
      return null;
    }

    const profile = new FeatureProfile();

    profile.id                                       = p.id;
    profile.name                                     = p.name;
    profile.loadTracking                             = p.load_tracking;
    profile.hotspotTracking                          = p.hotspot_tracking;
    profile.allowLoadsSearchFiltering                = p.allow_loads_search_filtering;
    profile.trailerYardManagement                    = p.trailer_yard_management;
    profile.multiCollections                         = p.multi_collections;
    profile.multiDeliveries                          = p.multi_deliveries;
    profile.tipAndReload                             = p.tip_and_reload;
    profile.tmsLoadsSynchronization                  = p.tms_loads_synchronization;
    profile.cabinLicensePlateRequired                = p.cabin_license_plate_required;
    profile.sapSignaturesSending                     = p.sap_signatures_sending;

    profile.assetManagement                          = p.asset_management;
    profile.quantitiesUpdateTimer                    = p.quantities_update_timer;
    profile.driverAssignmentModeFeature              = p.driver_assignment_mode_feature;
    profile.proofOfDeliveryRuleFeature               = p.pod_rule_feature;
    profile.photoPodRuleFeature                      = p.photo_pod_rule_feature;
    profile.dropAndHookFeature                       = p.drop_and_hook_feature;
    profile.automaticTripCreationFeature             = p.automatic_trip_creation_feature;
    profile.automaticTripCreationOrderTypes          = p.automatic_trip_creation_order_types;
    profile.sapMultiCollectionsCheckin               = p.sap_multi_collections_checkin;
    profile.autoGroupCollectionsFeature              = p.auto_group_collections_feature;
    profile.multiAccountsFeature                     = p.multi_accounts_feature;
    profile.overdueNotificationFeature               = p.overdue_notification_feature;
    profile.handoverManagementFeature                = p.handover_management_feature;
    profile.hideVehicleIdOnDriverInformationFeature  = p.hide_vehicle_id_on_driver_information_feature;
    profile.podValidationManufacturerSyncFeature     = p.pod_validation_manufacturer_sync_feature;
    profile.podValidationMrSyncFeature               = p.pod_validation_mr_sync_feature;
    profile.useCmaLiteOverCicoFeature                = p.use_cma_lite_over_cico_feature;
    profile.useCmaLiteOverCicoDeliveryRuleFeature    = p.use_cma_lite_over_cico_delivery_rule_feature;
    profile.tripCreatedWebhookEnrichedDataFeature    = p.trip_created_webhook_enriched_data_feature;
    profile.sortLoadsByTripIdFeature                 = p.sort_loads_by_trip_id_feature;
    profile.sortStopsByBlueyonderOrderFeature        = p.sort_stops_by_blueyonder_order_feature
    profile.etaComputationFeature                    = p.eta_computation_control_feature;
    profile.displayAutomaticEtaControlFeature        = p.display_automatic_eta_control_feature;
    profile.mrDropQuantityUpdateFeature              = p.mr_drop_quantity_update_control_feature;
    profile.retrieveCarrierEtaFromByFeature          = p.retrieve_carrier_eta_from_by_feature;
    profile.communicatePickEtaForMrFeature           = p.communicate_pick_eta_for_mr_feature;

    return profile;
  }

  static toDto(p: FeatureProfile): any {
    if (!p) {
      return null;
    }

    return {
      id: p.id,
      name: p.name,
      load_tracking: p.loadTracking,
      hotspot_tracking: p.hotspotTracking,
      allow_loads_search_filtering: p.allowLoadsSearchFiltering,
      trailer_yard_management: p.trailerYardManagement,
      multi_collections: p.multiCollections,
      multi_deliveries: p.multiDeliveries,
      tip_and_reload: p.tipAndReload,
      tms_loads_synchronization: p.tmsLoadsSynchronization,
      cabin_license_plate_required: p.cabinLicensePlateRequired,
      sap_signatures_sending: p.sapSignaturesSending,
      asset_management: p.assetManagement,
      quantities_update_timer: p.quantitiesUpdateTimer,
      driver_assignment_mode_feature: p.driverAssignmentModeFeature,
      pod_rule_feature: p.proofOfDeliveryRuleFeature,
      photo_pod_rule_feature: p.photoPodRuleFeature,
      drop_and_hook_feature: p.dropAndHookFeature,
      automatic_trip_creation_feature: p.automaticTripCreationFeature,
      automatic_trip_creation_order_types: p.automaticTripCreationOrderTypes,
      sap_multi_collections_checkin: p.sapMultiCollectionsCheckin,
      auto_group_collections_feature: p.autoGroupCollectionsFeature,
      multi_accounts_feature: p.multiAccountsFeature,
      overdue_notification_feature: p.overdueNotificationFeature,
      handover_management_feature: p.handoverManagementFeature,
      hide_vehicle_id_on_driver_information_feature: p.hideVehicleIdOnDriverInformationFeature,
      pod_validation_manufacturer_sync_feature: p.podValidationManufacturerSyncFeature,
      pod_validation_mr_sync_feature: p.podValidationMrSyncFeature,
      use_cma_lite_over_cico_feature: p.useCmaLiteOverCicoFeature,
      use_cma_lite_over_cico_delivery_rule_feature: p.useCmaLiteOverCicoDeliveryRuleFeature,
      trip_created_webhook_enriched_data_feature: p.tripCreatedWebhookEnrichedDataFeature,
      sort_loads_by_trip_id_feature: p.sortLoadsByTripIdFeature,
      sort_stops_by_blueyonder_order_feature : p.sortStopsByBlueyonderOrderFeature,
      eta_computation_control_feature: p.etaComputationFeature,
      display_automatic_eta_control_feature: p.displayAutomaticEtaControlFeature,
      mr_drop_quantity_update_control_feature: p.mrDropQuantityUpdateFeature,
      retrieve_carrier_eta_from_by_feature: p.retrieveCarrierEtaFromByFeature,
      communicate_pick_eta_for_mr_feature: p.communicatePickEtaForMrFeature,
    };
  }

  public static isFeatureEnabled(featureProfileEnabled: boolean, regionFeatureStatus: RegionFeatureStatus): boolean {
    switch (regionFeatureStatus) {
      case RegionFeatureStatus.FORCED:
        return true;
      case RegionFeatureStatus.AVAILABLE:
        return featureProfileEnabled;
      default:
        return false;
    }
  }

  public static isNewFeatureEnabled(feature: InheritableOnOffFeature, regionValue: boolean, regionOverrideAbility: RegionFeatureOverrideAbility): boolean {
    switch (regionOverrideAbility) {
      case RegionFeatureOverrideAbility.OVERRIDABLE:
        switch (feature) {
          case InheritableOnOffFeature.INHERITED:
            return regionValue;
          case InheritableOnOffFeature.ON:
            return true;
          case InheritableOnOffFeature.OFF:
          default:
            return false;
        }
      case RegionFeatureOverrideAbility.NOT_OVERRIDABLE:
        return regionValue;
      default:
        return false;
    }
  }

  applyRegionFeatures(regionFeatures: RegionFeatures) {
    this.loadTracking              = FeatureProfile.isFeatureEnabled(this.loadTracking, regionFeatures.loadTracking);
    this.hotspotTracking           = FeatureProfile.isFeatureEnabled(this.hotspotTracking, regionFeatures.hotspotTracking);
    this.allowLoadsSearchFiltering = FeatureProfile.isFeatureEnabled(this.allowLoadsSearchFiltering, regionFeatures.allowLoadsSearchFiltering);
    this.trailerYardManagement     = FeatureProfile.isFeatureEnabled(this.trailerYardManagement, regionFeatures.trailerYardManagement);
    this.multiCollections          = FeatureProfile.isFeatureEnabled(this.multiCollections, regionFeatures.multiCollections);
    this.multiDeliveries           = FeatureProfile.isFeatureEnabled(this.multiDeliveries, regionFeatures.multiDeliveries);
    this.tipAndReload              = FeatureProfile.isFeatureEnabled(this.tipAndReload, regionFeatures.tipAndReload);
    this.tmsLoadsSynchronization   = FeatureProfile.isFeatureEnabled(this.tmsLoadsSynchronization, regionFeatures.tmsLoadsSynchronization);
    this.cabinLicensePlateRequired = FeatureProfile.isFeatureEnabled(this.cabinLicensePlateRequired, regionFeatures.cabinLicensePlateRequired);
    this.sapSignaturesSending      = FeatureProfile.isFeatureEnabled(this.sapSignaturesSending, regionFeatures.sapSignaturesSending);
    this.assetManagement           = FeatureProfile.isFeatureEnabled(this.assetManagement, regionFeatures.assetManagement);
    this.quantitiesUpdateTimer     = FeatureProfile.isFeatureEnabled(this.quantitiesUpdateTimer, regionFeatures.quantitiesUpdateTimer);

    if (regionFeatures.driverAssignmentModeOverrideAbility === RegionFeatureOverrideAbility.NOT_OVERRIDABLE) {
      this.driverAssignmentModeFeature = DriverAssignmentMode.INHERITED;
    }

    if (regionFeatures.proofOfDeliveryRuleOverrideAbility === RegionFeatureOverrideAbility.NOT_OVERRIDABLE) {
      this.proofOfDeliveryRuleFeature = ProofOfDeliveryRule.INHERITED;
    }

    if (regionFeatures.tripCreatedWebhookEnrichedDataOverrideAbility === RegionFeatureOverrideAbility.NOT_OVERRIDABLE) {
      this.tripCreatedWebhookEnrichedDataFeature = InheritableOnOffFeature.INHERITED;
    }

    if (regionFeatures.photoPodRuleOverrideAbility === RegionFeatureOverrideAbility.NOT_OVERRIDABLE) {
      this.photoPodRuleFeature = PhotoPodRule.ONLY_AT_THE_DROP_LOCATION;
    }

    if (regionFeatures.dropAndHookOverrideAbility === RegionFeatureOverrideAbility.NOT_OVERRIDABLE) {
      this.dropAndHookFeature = InheritableOnOffFeature.INHERITED;
    }

    if (regionFeatures.automaticTripCreationOverrideAbility === RegionFeatureOverrideAbility.NOT_OVERRIDABLE ||
        this.automaticTripCreationFeature === InheritableOnOffFeature.INHERITED) {
      this.automaticTripCreationFeature = InheritableOnOffFeature.INHERITED;
      this.automaticTripCreationOrderTypes = regionFeatures.automaticTripCreationOrderTypes;
    }

    if (regionFeatures.automaticTripCreationOverrideAbility === RegionFeatureOverrideAbility.NOT_OVERRIDABLE) {
      this.automaticTripCreationFeature = InheritableOnOffFeature.INHERITED;
    }

    if (regionFeatures.sapMultiCollectionsOverrideAbility === RegionFeatureOverrideAbility.NOT_OVERRIDABLE) {
      this.sapMultiCollectionsCheckin = InheritableOnOffFeature.INHERITED;
    }

    if (regionFeatures.sapMultiCollectionsOverrideAbility === RegionFeatureOverrideAbility.NOT_OVERRIDABLE) {
      this.autoGroupCollectionsFeature = AutoGroupCollections.INHERITED;
    }

    if (regionFeatures.overdueNotificationOverrideAbility === RegionFeatureOverrideAbility.NOT_OVERRIDABLE) {
      this.overdueNotificationFeature = InheritableOnOffFeature.INHERITED;
    }

    if (regionFeatures.handoverManagementOverrideAbility === RegionFeatureOverrideAbility.NOT_OVERRIDABLE) {
      this.handoverManagementFeature = InheritableOnOffFeature.INHERITED;
    }

    if (regionFeatures.hideVehicleIdOnDriverInformationOverrideAbility === RegionFeatureOverrideAbility.NOT_OVERRIDABLE) {
      this.hideVehicleIdOnDriverInformationFeature = InheritableOnOffFeature.INHERITED;
    }

    if (regionFeatures.podValidationManufacturerSyncOverrideAbility === RegionFeatureOverrideAbility.NOT_OVERRIDABLE) {
      this.podValidationManufacturerSyncFeature = InheritableOnOffFeature.INHERITED;
    }

    if (regionFeatures.podValidationMrSyncOverrideAbility === RegionFeatureOverrideAbility.NOT_OVERRIDABLE) {
      this.podValidationMrSyncFeature = InheritableOnOffFeature.INHERITED;
    }

    if (regionFeatures.retrieveCarrierEtaFromByOverrideAbility === RegionFeatureOverrideAbility.NOT_OVERRIDABLE) {
      this.retrieveCarrierEtaFromByFeature = InheritableOnOffFeature.INHERITED;
    }

    if (regionFeatures.communicatePickEtaForMrOverrideAbility === RegionFeatureOverrideAbility.NOT_OVERRIDABLE) {
      this.communicatePickEtaForMrFeature = InheritableOnOffFeature.INHERITED;
    }
  }
}
